<template>
  <v-app>
    <v-main class="login-main">
      <v-container fluid class="fill-height">
        <v-row align="center" justify="center" class="fill-height">
          <v-col cols="12" sm="6" md="4" lg="3">
            <div class="text-center mb-8">
              <!-- Logo -->
              <v-avatar size="80" color="primary" class="mb-4">
                <v-icon size="40" color="white">mdi-domain</v-icon>
              </v-avatar>
              <!-- Title -->
              <h1 class="text-h4 font-weight-bold text-white mb-2">نظام الموارد البشرية</h1>
            </div>

            <!-- Login Card -->
            <v-card class="login-card" rounded="xl" elevation="24">
              <v-card-text class="pa-8">
                <div class="text-center mb-6">
                  <h2 class="text-h5 font-weight-medium">تسجيل الدخول</h2>
                </div>

                <v-form ref="form" v-model="valid" @submit.prevent="handleLogin">
                  <!-- Username/Email Field -->
                  <v-text-field
                    v-model="credentials.username"
                    :rules="emailRules"
                    label="اسم المستخدم"
                    prepend-inner-icon="mdi-account"
                    variant="outlined"
                    rounded="lg"
                    density="comfortable"
                    required
                    class="mb-4"
                    hide-details="auto"
                  ></v-text-field>

                  <!-- Password Field -->
                  <v-text-field
                    v-model="credentials.password"
                    :rules="passwordRules"
                    label="كلمة المرور"
                    prepend-inner-icon="mdi-lock"
                    :type="showPassword ? 'text' : 'password'"
                    variant="outlined"
                    rounded="lg"
                    density="comfortable"
                    :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append-inner="showPassword = !showPassword"
                    required
                    class="mb-6"
                    hide-details="auto"
                  ></v-text-field>

                  <!-- Error Alert -->
                  <v-alert
                    v-if="error"
                    type="error"
                    variant="tonal"
                    rounded="lg"
                    class="mb-6"
                    text
                  >
                    {{ error }}
                  </v-alert>

                  <!-- Login Button -->
                  <v-btn
                    color="primary"
                    size="large"
                    block
                    rounded="lg"
                    :loading="loading"
                    :disabled="!valid"
                    @click="handleLogin"
                    class="text-subtitle-1 font-weight-medium"
                    elevation="0"
                  >
                    دخول
                  </v-btn>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { ref, reactive, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'

export default {
  name: 'LoginComponent',
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const form = ref(null)
    const valid = ref(false)
    const showPassword = ref(false)

    const credentials = reactive({
      username: '',
      password: ''
    })

    const loading = computed(() => store.state.auth.loading)
    const error = computed(() => store.state.auth.error)

    const emailRules = [
      v => !!v || 'اسم المستخدم مطلوب'
    ]

    const passwordRules = [
      v => !!v || 'كلمة المرور مطلوبة'
    ]

    const handleLogin = async () => {
      const { valid } = await form.value.validate()
      if (!valid) return

      const result = await store.dispatch('auth/login', credentials)
      
      if (result.success) {
        const userRoles = store.getters['auth/userRoles']
        
        // Role-based redirection
        let redirectPath
        if (route.query.redirect) {
          redirectPath = route.query.redirect
        } else if (userRoles.includes('admin') || userRoles.includes('hr')) {
          redirectPath = '/' // Dashboard for HR/Admin
        } else if (userRoles.includes('employee')) {
          redirectPath = '/profile' // Profile for employees
        } else {
          redirectPath = '/'
        }
        
        router.push(redirectPath)
      }
    }

    return {
      form,
      valid,
      credentials,
      loading,
      error,
      showPassword,
      emailRules,
      passwordRules,
      handleLogin
    }
  }
}
</script>

<style scoped>
.login-main {
  background: linear-gradient(135deg, #009688 0%, #00897B 50%, #00695C 100%);
  min-height: 100vh;
  position: relative;
}

.login-main::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 25% 25%, rgba(0, 188, 212, 0.15) 0%, transparent 70%),
    radial-gradient(circle at 75% 75%, rgba(0, 150, 136, 0.15) 0%, transparent 70%);
  pointer-events: none;
}

.login-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 400px;
  margin: 0 auto;
}

/* RTL Icon Positioning */
.v-field__prepend-inner {
  margin-inline-end: 12px;
  margin-inline-start: 0;
}

.v-field__append-inner {
  margin-inline-start: 12px;
  margin-inline-end: 0;
}

/* Animation */
.login-card {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mobile Responsiveness */
@media (max-width: 600px) {
  .login-card {
    margin: 16px;
  }
  
  .v-card-text.pa-8 {
    padding: 24px !important;
  }
}
</style> 